<template>
  <div class="app-order-details" ref="orderDetails">
    <div v-if="orderDetail.id" class="app-order-details-grid">
      <div class="app-order-details-grid-map py-3 pl-2">
        <v-card elevation="0" class="rounded-lg px-2 pa-2">
          <v-row>
            <v-col cols="6" class="d-flex align-center">
              <h2
                class="mr-2 text-caption text-lg-h6"
                v-if="orderDetail && orderDetail.reference_number"
              >
                {{ orderDetail.reference_number }}
              </h2>
              <v-chip
                class="mr-1 text-capitalize font-weight-bold"
                x-small
                v-if="orderDetail.verbose_status"
                :class="
                  $helper.getOrderStatusColor(orderDetail.verbose_status, true)
                "
              >
                {{ orderDetail.verbose_status }}
              </v-chip>
            </v-col>

            <v-col
              cols="6"
              class="text-right d-flex justify-end"
              v-if="orderDetail.branch"
            >
              <div class="mr-2">
                <TripTooltipWithIcon
                  v-if="orderDetail.customer_barcode_scanning || orderDetail.warehouse_barcode_scanning "
                  icon="mdi-barcode"
                  :text="orderDetail.barcode_number"
                  label="Barcode Number"
                />
              </div>
              <div class="mr-2">
                <TripTooltipWithIcon
                  icon="mdi-package-variant-closed"
                  :text="orderDetail.branch"
                  :label="`${configurationModule.branch} Name`"
                />
              </div>
              <div class="mr-2" v-if="orderDetail.execution_date">
                <TripTooltipWithIcon
                  icon="mdi-calendar-range"
                  :text="orderDetail.execution_date"
                  :label="`${orderModule.order} Date`"
                />
              </div>
            </v-col>
          </v-row>

          <div class="d-flex justify-space-between align-center py-2">
            <div class="align-center align-self-end d-flex pb-1">
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <span
                    class="text-body-2 mr-1 font-weight-bold pl-2"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ orderDetail.otp }}
                  </span>
                </template>
                <span class="text-capitalize">OTP</span>
              </v-tooltip>
              <CopyToClipboard
                v-if="orderDetail.otp_verification"
                :textTobeCopied="orderDetail.otp"
                iconClass="ml-2"
              />
              
              <PrimaryButton
                id="otpResend"
                v-if="
                  permissions &&
                  permissions.change &&
                  orderDetail &&
                  orderDetail.otp_verification &&
                  orderDetail?.trip_details?.status === 'active' &&
                  orderDetail.status !== 'delivered'
                "
                small
                btnClass="ma-1 rounded-lg"
                @click="resendOtp"
                icon="mdi-send"
                name="Re-send OTP"
              />
              <div class="mr-2">
                <TripTooltipWithIcon
                  v-if="
                    orderDetail.bypass_otp_verification &&
                    orderDetail.bypass_otp_verification_reason
                  "
                  icon="mdi-information"
                  :label="orderDetail.bypass_otp_verification_reason"
                />
              </div>
            </div>

            <div class="px-2 d-flex justify-end">
              <PrimaryButton
                id="ordersWayBillPdf"
                :x-small="$vuetify.breakpoint.xs"
                :small="true"
                btnClass="ma-1 rounded-lg"
                @click="getOrderWayBillPDFDownload(orderDetail.id)"
                icon="mdi-file-pdf-box"
                name="Way Bill Pdf"
              />
              <PrimaryButton
                id="ordersPOD"
                :x-small="$vuetify.breakpoint.xs"
                :small="true"
                v-if="orderDetail.is_pod_uploaded"
                btnClass="ma-1 rounded-lg"
                @click="getOrderAttachmentPODDownload(orderDetail.id)"
                icon="mdi-file-pdf-box"
                name="POD"
              />

              <PrimaryButton
                id="ordersEdit"
                :x-small="$vuetify.breakpoint.xs"
                :small="true"
                btnClass="ma-1 rounded-lg"
                v-if="
                  permissions &&
                  permissions.change &&
                  orderDetail &&
                  orderDetail.action_details &&
                  orderDetail.action_details.is_editable
                "
                @click="editOrder"
                icon="mdi-pencil"
                name="Edit"
              />

              <PrimaryButton
                id="ordersReschedule"
                v-if="
                  permissions &&
                  permissions.change &&
                  orderDetail &&
                  orderDetail.action_details &&
                  orderDetail.action_details.is_reschedulable
                "
                small
                btnClass="ma-1 rounded-lg"
                @click="rescheduleOrder"
                icon="mdi-sync"
                name="Reschedule"
              />

              <ErrorButton
                id="ordersCancel"
                v-if="
                  permissions &&
                  permissions.change &&
                  orderDetail &&
                  orderDetail.action_details &&
                  orderDetail.action_details.is_cancellable
                "
                small
                btnClass="ma-1 rounded-lg"
                depressed
                @click="cancelOrder"
                color="error"
                icon="mdi-cancel"
                name="Cancel"
              />

              <PrimaryButton
                id="ordersAttachments"
                small
                btnClass="ma-1 rounded-lg"
                depressed
                icon="mdi-paperclip"
                name="Order Attachments"
                @click="openPODDialog = true"
                v-if="
                  (permissions &&
                    permissions.change &&
                    orderDetail &&
                    orderDetail.status == 'delivered') ||
                  orderDetail.status == 'partially_delivered'
                "
                color="primary lighten-3"
              />
            </div>
          </div>
        </v-card>

        <v-card class="my-3 rounded-lg" elevation="0">
          <v-row no-gutters>
            <v-col cols="8" class="d-flex align-center pl-1">
              <div
                v-if="
                  orderDetail &&
                  orderDetail.get_order_warning &&
                  Object.keys(orderDetail.get_order_warning).length > 0
                "
              >
                <v-icon color="error" class="ml-2 mr-2" small
                  >mdi-information</v-icon
                >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span
                      v-bind="attrs"
                      v-on="on"
                      class="text-caption error--text"
                    >
                      {{ orderDetail.get_order_warning.message }}
                    </span>
                  </template>
                  <span
                    class="text-capitalize"
                    v-if="orderDetail.get_order_warning.code"
                  >
                    {{ orderDetail.get_order_warning.code.replace(/_/g, " ") }}
                  </span>
                </v-tooltip>
              </div>
            </v-col>
            <v-col cols="4">
              <v-tabs
                v-model="orderDetailsTab"
                height="30px"
                right
                class="rounded-lg pa-0"
              >
                <v-tab>
                  <h4
                    class="text-caption font-weight-bold pa-0 ma-0 app-driver-font-tab"
                  >
                    Details
                  </h4>
                </v-tab>

                <v-tab>
                  <h4
                    class="text-caption font-weight-bold pa-0 ma-0 app-driver-font-tab"
                  >
                    Map
                  </h4>
                </v-tab>
              </v-tabs>
            </v-col>
          </v-row>
        </v-card>

        <div ref="mapCard">
          <v-tabs-items
            v-model="orderDetailsTab"
            class="rounded-lg background_color mb-3 mt-0"
          >
            <v-tab-item
              :style="`height: calc(100vh -  ${isActions ? '395px' : '410px'})`"
              class="overflow-y-auto"
            >
              <div class="h-100 d-flex flex-column">
                <v-card
                  elevation="0"
                  class="pa-0 w-100 h-100 rounded-lg mb-2"
                  style="height: 33px"
                >
                  <v-card-text class="px-3 py-2 d-flex d-flex algin-center">
                    <div class="d-flex align-center">
                      <span class="mr-2 text-caption"
                        >{{ orderModule.order }} Segment</span
                      >
                      <v-chip
                        label
                        x-small
                        color="error"
                        class="mr-2 px-2 text-capitalize"
                      >
                        <b>{{ orderDetail.segment }}</b>
                      </v-chip>
                    </div>
                    <div
                      class="d-flex align-center"
                      v-if="orderDetail.segment == 'sales'"
                    >
                      <span class="mr-2 text-caption"
                        >{{ orderModule.order }} Type</span
                      >
                      <v-chip
                        label
                        x-small
                        class="px-2 text-capitalize"
                        :color="
                          orderDetail.order_type == 'Pickup'
                            ? 'warning'
                            : 'success'
                        "
                      >
                        <b>{{ orderDetail.order_type }}</b>
                      </v-chip>
                    </div>
                  </v-card-text>
                </v-card>

                <v-row class="ma-0">
                  <v-col cols="6" class="pr-2 pt-1 pl-0">
                    <CardOrderCustomer
                      :dataObj="orderDetail.customer_details"
                    />
                  </v-col>
                  <v-col class="pl-2 pt-1 pr-0">
                    <CardOrderDetails :dataObj="orderDetail.order_details" />
                  </v-col>
                  <v-col
                    cols="6"
                    class="pr-2 pt-1 pl-0 pb-0"
                    v-if="$helper.isCompanyHasItems()"
                  >
                    <CardOrderItem :dataObj="orderDetail.item_details" />
                  </v-col>
                  <v-col
                    :cols="$helper.isCompanyHasItems() ? '6' : '12'"
                    class="pl-2 pt-1 pr-0 pb-0"
                  >
                    <CardOrderProcessing
                      :dataObj="orderDetail.processing_time_details"
                    />
                  </v-col>
                </v-row>
              </div>
            </v-tab-item>
            <v-tab-item
              :style="`height: calc(100vh - ${isActions ? '395px' : '410px'})`"
              class=""
            >
              <GoogleMap
                :hasMapInfo="true"
                :mapHeight="`calc(100vh - ${isActions ? '395px' : '410px'})`"
                mapWidth="100%"
                ref="orderMap"
                mapId="orderMap"
              >
                <template #mapInfo>
                  <MapInfo legendType="orders" />
                </template>
              </GoogleMap>
            </v-tab-item>
          </v-tabs-items>
        </div>

        <v-row no-gutters class="d-flex pa-0 ma-0">
          <v-col cols="6" class="d-flex pr-2 pt-1">
            <CardOrderWeight
              :dataObj="orderDetail.order_statics"
              @orderItemList="getItemList"
            />
          </v-col>
          <v-col cols="6" class="d-flex pl-2 pt-1">
            <CardOrderTrip
              :dataObj="orderDetail.trip_details"
              @redirectTrip="redirectTrip"
            />
          </v-col>
        </v-row>
      </div>
      <div class="pa-2">
        <v-card
          elevation="0"
          class="rounded-lg px-4 py-3 d-flex justify-space-between sticky-top mb-3 mt-1"
          style="z-index: 4"
        >
          <h5 class="text-subtitle-2">Details</h5>
          <div>
            <SecondaryButton
              id="ordersRemarks"
              x-small
              btnClass="mr-2 text-uppercase rounded-lg"
              @click="showRemarks"
              v-if="
                orderDetail &&
                orderDetail.remarks &&
                (orderDetail.remarks.remarks ||
                  orderDetail.remarks.order_remarks ||
                  orderDetail.remarks.customer_remarks ||
                  orderDetail.remarks.driver_remarks)
              "
              name="Remarks"
            >
            </SecondaryButton>
            <SecondaryButton
              id="CustomFieldCard"
              x-small
              btnClass="mr-2 text-uppercase rounded-lg"
              depressed
              v-show="orderDetail && orderDetail.custom_fields"
              @click="showField"
              name="Extra Field"
            >
            </SecondaryButton>
            <SecondaryButton
              id="ordersLogs"
              x-small
              btnClass="mr-2 text-uppercase rounded-lg"
              depressed
              @click="showLogs"
              name="Logs"
            >
            </SecondaryButton>
            <SecondaryButton
              id="ordersAddress"
              btnClass="text-uppercase rounded-lg"
              x-small
              @click="showAddress"
              name="Address"
            >
            </SecondaryButton>
          </div>
        </v-card>

        <div
          class="overflow-y-auto"
          style="height: calc(100vh - 152px)"
          ref="orderLeftPanel"
        >
          <v-expand-transition>
            <div v-show="isVisibleAddress">
              <CardOrderAddresses :dataObj="orderDetail" />
            </div>
          </v-expand-transition>

          <v-expand-transition>
            <div v-show="isVisibleCustomField">
              <v-card
                elevation="0"
                class="rounded-lg mt-4"
                v-if="orderDetail && orderDetail.custom_fields"
              >
                <v-card-title class="px-3 py-1">
                  <v-row no-gutters>
                    <v-col cols="12">
                      <h6>Extra Information</h6>
                    </v-col>
                  </v-row>
                </v-card-title>
                <v-divider></v-divider>

                <v-card-subtitle>
                  <div class="overflow-y-auto" style="height: 170px">
                    <div
                      v-for="(value, key, index) in orderDetail.custom_fields"
                      :key="index"
                      class="pt-1 px-2"
                    >
                      <v-row no-gutters>
                        <v-col cols="6">
                          <h5 class="text-left text-capitalize" v-if="key">
                            {{ key.replace(/_/g, "  ") }}
                          </h5>
                        </v-col>
                        <v-col cols="6">
                          <h6
                            class="text-caption black--text text-center"
                            v-if="Array.isArray(value)"
                          >
                            <div
                              v-for="(itemKey, itemIndex) in value"
                              :key="itemIndex"
                            >
                              <v-chip
                                x-small
                                class="success px-2 text-capitalize"
                              >
                                {{ itemKey }}
                              </v-chip>
                            </div>
                          </h6>
                          <h6
                            class="text-caption black--text text-center"
                            v-else-if="value == 'true' || value == 'false'"
                          >
                            <v-icon
                              small
                              :color="value == 'true' ? 'success' : 'error'"
                            >
                              {{
                                value == "true"
                                  ? "mdi-check-circle"
                                  : "mdi-close-circle"
                              }}
                            </v-icon>
                          </h6>
                          <h6
                            class="text-caption black--text text-center"
                            v-else
                          >
                            {{ value }}
                          </h6>
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                </v-card-subtitle>
              </v-card>
            </div>
          </v-expand-transition>

          <v-expand-transition>
            <div v-show="isVisibleLog">
              <v-card
                elevation="0"
                class="rounded-lg pa-0 mt-4 h-100 app-relative"
              >
                <v-card-title class="py-1 text-capitalize">
                  <h5 class="text-caption py-2 font-weight-bold">Logs</h5>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-subtitle
                  class="pa-0 overflow-y-auto custom-small-scroll"
                  style="height: 50vh"
                >
                  <Logs :logs="logs" v-if="logs && logs.length > 0" />
                  <div class="h-100" v-else>
                    <h5
                      class="font-weight-bold text_color--text text-center my-16"
                    >
                      There is no log available
                    </h5>
                  </div>
                </v-card-subtitle>
              </v-card>
            </div>
          </v-expand-transition>

          <v-expand-transition>
            <div v-show="isVisibleRemarks">
              <div
                v-for="(value, key, index) in orderDetail.remarks"
                :key="index"
              >
                <v-card
                  v-if="value"
                  elevation="0"
                  class="rounded-lg pa-4 w-100 mt-3 warning lighten-3"
                >
                  <v-row
                    class="warning lighten-5"
                    style="transition: all 0.3s ease"
                  >
                    <v-col cols="2" class="text-center">
                      <v-icon color="accent darken-1">mdi-information</v-icon>
                    </v-col>
                    <v-col cols="10">
                      <h5 class="mb-1 text-capitalize">
                        {{ key.replace(/_/g, "  ") }}
                      </h5>
                      <h6 class="font-weight-light">
                        {{ value }}
                      </h6>
                    </v-col>
                  </v-row>
                </v-card>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </div>
      <div class="d-none">
        <PodList
          v-if="orderDetail.id"
          v-model="openPODDialog"
          :podList="orderDetail.pod_details"
          :orderID="orderDetail.id"
          :orderType="orderDetail.order_type"
          @refreshList="getOrderData(orderDetail.id)"
        />

        <OrderItemList
          ref="orderItem"
          v-model="openOrderItemDialog"
          :orderId="orderDetail.id"
          :status="orderDetail.status"
        />
        <CancelOrderForm
          :data="selectedCancelOrders"
          :isOrderTracking="false"
          orderMethod="cancelOrder"
          orderModule="orderNew"
          v-model="openCancelOrderDialog"
          @refreshList="
            getOrderData(orderDetail.id);
            sendParent(true);
          "
        />

        <RescheduleOrderForm
          :orderParentObj="orderDetail"
          orderMethod="rescheduleOrder"
          orderModule="orderNew"
          v-model="openRescheduleOrderDialog"
          @refreshList="
            getOrderData(orderDetail.id);
            sendParent(true);
          "
        />
      </div>
    </div>
    <div v-else>
      <GoogleMap
        :hasMapInfo="true"
        ref="orderMapNew"
        mapId="orderMapNew"
        class="mt-4"
        mapHeight="calc(100vh - 95px)"
      >
        <template #mapInfo>
          <MapInfo legendType="orders" />
        </template>
      </GoogleMap>
    </div>
  </div>
</template>

<script>
import { bus } from "@/main";
import GoogleMap from "@/components/BaseComponents/GoogleMap.vue";
import MapInfo from "@/components/BaseComponents/MapInfo.vue";
import AssetChangeForm from "@/components/ModuleBasedComponents/Trip/AssetChangeForm.vue";
import TripTooltipWithIcon from "@/components/ModuleBasedComponents/Trip/TripTooltipWithIcon.vue";
import BulkUpload from "@/components/BaseComponents/BulkUpload.vue";
import CancelOrderForm from "@/components/ModuleBasedComponents/Order/CancelOrderForm.vue";
import RescheduleOrderForm from "@/components/ModuleBasedComponents/Order/RescheduleOrderForm.vue";
import OrderItemList from "../Operation/OrderItemList.vue";
import Logs from "@/components/BaseComponents/Logs.vue";
import CopyToClipboard from "@/components/GeneralComponents/CopyToClipboard.vue";
import {
  configurationModule,
  customerModule,
  orderModule,
  tripModule,
  fleetModule,
  itemModule,
} from "@/utils/moduleMapping";
import PodList from "@/components/ModuleBasedComponents/Order/PodList.vue";
import CardOrderCustomer from "@/components/ModuleBasedComponents/Order/CardOrderCustomer.vue";
import CardOrderDetails from "@/components/ModuleBasedComponents/Order/CardOrderDetails.vue";
import CardOrderItem from "@/components/ModuleBasedComponents/Order/CardOrderItem.vue";
import CardOrderProcessing from "@/components/ModuleBasedComponents/Order/CardOrderProcessing.vue";
import CardOrderWeight from "@/components/ModuleBasedComponents/Order/CardOrderWeight.vue";
import CardOrderTrip from "@/components/ModuleBasedComponents/Order/CardOrderTrip.vue";
import CardPaymentCollection from "@/components/ModuleBasedComponents/Order/CardPaymentCollection.vue";
import CardOrderAddresses from "./CardOrderAddresses.vue";

export default {
  props: {
    id: {
      type: [Number, null],
    },
  },
  components: {
    CopyToClipboard,
    OrderItemList,
    GoogleMap,
    MapInfo,
    AssetChangeForm,
    TripTooltipWithIcon,
    BulkUpload,
    CancelOrderForm,
    RescheduleOrderForm,
    Logs,
    PodList,
    CardOrderCustomer,
    CardOrderDetails,
    CardOrderItem,
    CardOrderProcessing,
    CardOrderWeight,
    CardOrderTrip,
    CardPaymentCollection,
    CardOrderAddresses,
  },
  data() {
    return {
      fleetModule,
      configurationModule,
      orderModule,
      tripModule,
      itemModule,
      openPODDialog: false,
      customerModule,
      orderDetailsTab: 0,
      statusNumber: 1,
      orderList: [],
      statusList: [
        "Unassigned",
        "Assigned",
        "Picked up",
        "Out For Delivered",
        "Delivered",
      ],
      orderDetail: {},
      search: null,
      logs: [],
      selectedCancelOrders: [],
      headers: [
        {
          value: "reference_number",
          align: "start",
          text: "Reference Number",
        },
        {
          value: "status",
          align: "start",
          text: "Status",
        },
        {
          value: "order_type",
          align: "start",
          text: "Order Type",
        },
        {
          value: "total_quantity",
          align: "start",
          text: "Total Quantity",
        },
        {
          value: "volume",
          align: "start",
          text: "Volume",
        },
        {
          value: "weight",
          align: "start",
          text: "Weight",
        },
        {
          value: "address",
          align: "start",
          text: "Address",
        },
      ],

      isVisibleRemarks: false,
      isVisibleLog: true,
      isVisibleAddress: true,
      isVisibleCustomField: true,
      openCancelOrderDialog: false,
      openRescheduleOrderDialog: false,
      openOrderItemDialog: false,
    };
  },
  watch: {
    isVisibleRemarks(val) {
      if (val) {
        setTimeout(() => {
          this.$refs.orderLeftPanel.scrollTop =
            this.$refs.orderLeftPanel?.clientHeight;
        }, 200);
      }
    },
    id(val) {
      if (val) {
        this.getOrderData(val);
      }
    },
    orderDetailsTab(val) {
      if (val == 1) {
        this.setOrderLocationOnMap();
      }
    },
  },
  computed: {
    isActions() {
      return (
        this.orderDetail &&
        this.orderDetail.action_details &&
        (!this.orderDetail.action_details.is_cancellable ||
          !this.orderDetail.action_details.is_editable ||
          !this.orderDetail.action_details.is_reschedulable)
      );
    },
  },
  methods: {
    redirectTrip(trip) {
      if (this.$route && this.$route.name != "trip-new") {
        this.$router.push({
          name: "trip-new",
          params: {
            data: {
              id: trip.trip_id,
              reference_number: trip.reference_number,
            },
          },
        });
      } else {
        this.$emit("action");
      }
    },
    redirectCustomerDetails(customerDetails) {
      if (customerDetails && customerDetails.id) {
        this.$router.push(`/app/client/customer/details/${customerDetails.id}`);
      }
    },
    getItemList() {
      this.openOrderItemDialog = true;
      this.$refs.orderItem.getItemList(this.orderDetail.id);
    },
    sendParent() {
      this.$emit("getList", true);
    },
    cancelOrder() {
      this.selectedCancelOrders = [this.orderDetail.id];
      this.openCancelOrderDialog = true;
    },
    rescheduleOrder() {
      this.openRescheduleOrderDialog = true;
    },
    showRemarks() {
      this.isVisibleRemarks = !this.isVisibleRemarks;
    },
    showLogs() {
      this.isVisibleLog = !this.isVisibleLog;
    },
    showField() {
      this.isVisibleCustomField = !this.isVisibleCustomField;
    },
    showAddress() {
      this.isVisibleAddress = !this.isVisibleAddress;
    },
    getOrderData(id) {
      this.getOrderLogs(id);
      this.getOrderView(id);
    },
    setOrderLocationOnMap() {
      let data = {
        ...this.orderDetail,
      };

      setTimeout(() => {
        this.$refs.orderMap.clearMarker();
        data.drop_details.title = "order details";
        if (
          data.drop_details &&
          data.drop_details.coordinates &&
          Object.keys(data.drop_details.coordinates).length > 0
        ) {
          this.$refs.orderMap.setCustomMaker(
            { ...data.drop_details, type: "drop", icon: "drop_order" },
            "destination"
          );
        }
        if (data.pickup_details && data.pickup_details.length > 0) {
          data.pickup_details.forEach((location) => {
            if (
              location &&
              location.coordinates &&
              Object.keys(location.coordinates).length > 0
            ) {
              this.$refs.orderMap.setCustomMaker(
                { ...location, type: "pickup", icon: "pickup_order" },
                "source"
              );
            }
          });
        }
        this.$refs.orderMap.setMapZoom(8);
      }, 400);
    },
    getOrderAttachmentPODDownload(id) {
      bus.$emit("showLoader", true);
      this.$api.orderNew
        .getOrderAttachmentPODDownload({ order: id })
        .then((res) => {
          bus.$emit("showLoader", false);
          let generatedPDFBlob = new Blob([res.data], {
            type: "application/pdf",
          });
          var pdfUrl = URL.createObjectURL(generatedPDFBlob);
          window.open(pdfUrl);
        })
        .catch((error) => {
          bus.$emit("showLoader", false);
          console.error(error);
        });
    },
    getOrderWayBillPDFDownload(id) {
      bus.$emit("showLoader", true);
      this.$api.orderNew
        .getOrderWayBillPDFDownload(id)
        .then((res) => {
          bus.$emit("showLoader", false);
          let generatedPDFBlob = new Blob([res.data], {
            type: "application/pdf",
          });
          var pdfUrl = URL.createObjectURL(generatedPDFBlob);
          window.open(pdfUrl);
        })
        .catch((error) => {
          bus.$emit("showLoader", false);
          console.error(error);
        });
    },
    getOrderView(id) {
      bus.$emit("showLoader", true);
      this.$api.orderNew
        .getOrderView(id)
        .then((res) => {
          bus.$emit("showLoader", false);
          this.orderDetail = res.data;
          this.isVisibleRemarks = false;
          if (this.orderDetailsTab == 1) {
            this.setOrderLocationOnMap();
          }

          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    getOrderLogs(id) {
      const params = {
        order: id,
        limit: "all",
      };
      this.$api.logs
        .getOrderLogs(params)
        .then((res) => {
          this.logs = res.data;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    resendOtp() {
      let data = {
        id: this.orderDetail.id,
      };
      this.$api.orderNew
        .resendOTP(data)
        .then((res) => {
          bus.$emit("showLoader", false);
          bus.$emit("showToastMessage", {
            message: "OTP send successfully",
            color: "success",
          });

          bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          bus.$emit("showLoader", false);
        });
    },
    editOrder() {
      if (this.orderDetail.segment == "sales") {
        if (this.orderDetail.order_type == "delivery") {
          this.$router.push({
            name: "edit-delivery-sale-order",
            params: { id: this.orderDetail.order_details.id },
          });
        } else {
          this.$router.push({
            name: "edit-pickup-sale-order",
            params: { id: this.orderDetail.order_details.id },
          });
        }
      } else {
        this.$router.push({
          name: "edit-adhoc-order",
          params: { id: this.orderDetail.order_details.id },
        });
      }
    },
  },
  mounted() {
    if (this.id && typeof this.id == typeof 1) {
      this.getOrderData(this.id);
    }
    // this.setMapFixed();
  },
  created() {
    this.permissions = this.$permissions.getPermission("order");
  },
};
</script>

<style>
.app-driver-font-tab {
  font-family: roboto;
  text-transform: capitalize;
  letter-spacing: 0.1px;
}
.hover-pointe {
  cursor: pointer;
}
.app-order-details {
  width: 100%;
}
.app-order-details-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  height: calc(100vh - 70px);
  overflow-y: auto;
}

@media screen and (max-width: 1361px) {
  .app-order-details-grid {
    grid-template-columns: 1fr 300px;
    padding: 8px;
  }
}
.app-order-details-grid-map {
  grid-row-gap: 17px;
  overflow: hidden;
}
/* .app-order-details-grid-map > .v-card {
  border: 1px solid red;
} */
.app-order-details-grid-details {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px);
  overflow-y: auto;
}

.v-expansion-panel-header {
  min-height: 30px !important;
}
.sticky-top {
  position: sticky;
  top: 0;
  z-index: 3;
}

.app-logs::-webkit-scrollbar {
  width: 10px;
}

.app-logs::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
}

.app-logs::-webkit-scrollbar-thumb {
  background-color: var(--v-primary-base) !important;
}
.app-order-address .v-timeline::before {
  height: calc(100% - 92px);
  top: 32px;
}
.app-order-address-done .v-timeline::before {
  height: 00%;
}
</style>
